import {format} from 'date-fns'

import DeliveryTruckSvg from '@/assets/food/icons/delivery-truck.svg?react'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import type {GrubhubOrder, Option} from '@/lib/food-grubhub/foodGrubhubModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './FoodOrderItem.module.scss'
import {useRestaurantInfo} from './RestaurantInfo'

interface FoodOrderItemProps {
  readonly order: GrubhubOrder
  readonly showDetails?: boolean
}

export default function FoodOrderItem({order, showDetails = false}: FoodOrderItemProps) {
  const navigate = useNavigate()

  const restaurantId = order.charges.lines.lineItems[0]?.restaurantId
  const orderStatus = order.status.toUpperCase()
  const isFinalized = orderStatus === 'FINALIZED'
  const isDelivered = orderStatus === 'DELIVERED'

  const {renderRestaurantInfo, renderRestaurantInfoDetails, renderFoodImage} = useRestaurantInfo({
    restaurantId,
  })

  const formatDate = (date: Date, pattern: string) => {
    try {
      return format(date, pattern)
    } catch {
      return 'Invalid date'
    }
  }

  const getCustomizations = (options: Option[]): string => {
    const allCustomizations: string[] = []

    const processOption = (option: Option) => {
      if (option.name) {
        if (option.quantity > 1) {
          allCustomizations.push(`${option.quantity}x ${option.name}`)
        } else {
          allCustomizations.push(option.name)
        }
      }

      if (option.childOptions && option.childOptions.length > 0) {
        option.childOptions.forEach(childOption => {
          processOption(childOption)
        })
      }
    }

    options.forEach(option => {
      processOption(option)
    })

    return allCustomizations.join(', ')
  }

  const formatStatus = (status: string): string => {
    return status
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Manual total calculation: dinerGrandTotal + Change to NFB - Discount
  const calculateCorrectTotal = (order: GrubhubOrder): string => {
    const baseTotal = parseFloat(order.charges.dinerGrandTotal)
    const changeToNFB = parseFloat(order.moreInfo.amountRoundUp)
    const discount = parseFloat(order.moreInfo.amountDiscount)

    const correctTotal = baseTotal + changeToNFB - discount
    return correctTotal.toFixed(2)
  }

  const renderDeliveredStatus = () => (
    <>
      <div className={style.Header}>
        <h3 className={style.DeliveryHeaderText}>
          Delivered on {formatDate(order.updatedAt, 'EEE, MMM d h:mm a')}
        </h3>
      </div>
      <div className={style.DeliveryStatus}>
        <div className={style.StatusBadge}>
          <span className={style.BadgeText}>
            <VisuallyHidden>Status: Delivered</VisuallyHidden>
            <span aria-hidden='true'>Delivered</span>
          </span>
        </div>
      </div>
    </>
  )

  const renderTrackingStatus = () => (
    <>
      <div className={style.DeliveryStatusActive}>
        <div className={style.DeliveryTime}>
          Estimated delivery: {order.orderingInfo.estimateRange.minimum} -{' '}
          {order.orderingInfo.estimateRange.maximum} minutes
        </div>
      </div>
      <div className={style.DeliveryStatus}>
        <div className={`${style.StatusBadge} ${style.Tracking}`}>
          <DeliveryTruckSvg
            aria-hidden='true'
            width={16}
            height={16}
            className={style.DeliveryTruckIcon}
          />
          <span className={style.BadgeText}>
            <VisuallyHidden>Status: Out for delivery</VisuallyHidden>
            <span aria-hidden='true'>Out for delivery</span>
          </span>
        </div>
      </div>
    </>
  )

  const renderOtherStatus = () => (
    <div className={style.DeliveryStatus}>
      <div className={style.StatusBadge}>
        <span className={style.BadgeText}>
          <VisuallyHidden>Status: {formatStatus(order.status)}</VisuallyHidden>
          <span aria-hidden='true'>{formatStatus(order.status)}</span>
        </span>
      </div>
    </div>
  )

  const getStatusContent = () => {
    if (isDelivered) {
      return renderDeliveredStatus()
    }
    if (isFinalized) {
      return renderTrackingStatus()
    }
    return renderOtherStatus()
  }

  return (
    <div className={`${style.Order} ${showDetails ? style.DetailsView : ''}`}>
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <div>
            <h2 className={style.OrderId} aria-label={`Food order ${order.id}`}>
              Order #{order.id}
            </h2>
            <div className={style.Time}>
              {showDetails
                ? `Placed on ${formatDate(order.createdAt, 'EEE, MMM d h:mm a')}`
                : `Placed on ${formatDate(order.createdAt, 'dd MMM yyyy')}`}
            </div>
            <div className={style.Time}>
              {showDetails &&
                isDelivered &&
                `Delivered on ${formatDate(order.updatedAt, 'EEE, MMM d h:mm a')}`}
              {showDetails && !isDelivered && !isFinalized && formatStatus(order.status)}
            </div>
          </div>
          <div className={style.TotalPrice}>
            <h3 aria-label='Order total'>Total Price</h3>
            <div>${calculateCorrectTotal(order)}</div>
          </div>
          <div>
            <VisuallyHidden>
              <h3>Delivery Address</h3>
            </VisuallyHidden>
            <div
              className={style.ShippingInfo}
              aria-label={`Delivery to: ${order.shippingInfo.name}, ${order.shippingInfo.line1}, ${order.shippingInfo.city}, ${order.shippingInfo.state} ${order.shippingInfo.postalCode}`}
            >
              <div className={style.Name} aria-hidden='true'>
                <span>Deliver to: </span>
                {order.shippingInfo.name}
              </div>
              <div className={style.Address} aria-hidden='true'>
                <div className={style.AddressItem}>{order.shippingInfo.line1}</div>
                <div className={style.AddressItem}>
                  {order.shippingInfo.city}, {order.shippingInfo.state}{' '}
                  {order.shippingInfo.postalCode}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={style.StoreInfo}>
          <VisuallyHidden>
            <h3>Restaurant</h3>
          </VisuallyHidden>
          {showDetails ? renderRestaurantInfoDetails() : renderRestaurantInfo()}
        </div>
        <div className={style.FoodItems}>
          <VisuallyHidden>
            <h3>Order Items</h3>
          </VisuallyHidden>
          <div className={style.ItemsList}>
            {order.charges.lines.lineItems.map((item, index) => {
              const customizations = item.options ? getCustomizations(item.options) : ''
              return (
                <div key={item.menuItemId || index} className={style.ItemRow}>
                  <div className={style.ItemProduct}>
                    <div className={style.FoodItem}>
                      <div className={style.FoodItemImage}>
                        {renderFoodImage(item.menuItemId, item.name)}
                      </div>
                      <div className={style.FoodItemDetails}>
                        <div className={style.FoodItemName}>
                          <span>{item.name}</span>
                        </div>
                        {customizations && (
                          <div className={style.FoodItemNote}>
                            <VisuallyHidden>Customizations</VisuallyHidden>
                            {customizations}
                          </div>
                        )}
                        {showDetails && (
                          <div className={style.ItemPriceMobile} aria-hidden='true'>
                            <span>${item.dinerTotal}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={style.ItemQuantity}>
                    <VisuallyHidden>Quantity</VisuallyHidden>
                    {item.quantity}
                  </div>
                  {showDetails && (
                    <div className={style.ItemPrice} aria-label={`Price: $${item.dinerTotal}`}>
                      <span aria-hidden='true'>${item.dinerTotal}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        {showDetails && (
          <div className={style.OrderSummary}>
            <div className={style.OrderSummaryHeader}>
              <div className={style.SummaryLabel}>Order Summary</div>
            </div>
            <VisuallyHidden>
              <h3>Order Summary</h3>
            </VisuallyHidden>
            <div className={style.SummaryItems}>
              <div
                className={style.SummaryItem}
                aria-label={`Subtotal: $${order.charges.dinerSubtotal}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Subtotal ({order.charges.lines.lineItems.length} item
                  {order.charges.lines.lineItems.length !== 1 ? 's' : ''})
                </span>
                <span className={style.SummaryValue} aria-hidden='true'>
                  ${order.charges.dinerSubtotal}
                </span>
              </div>
              <div
                className={style.SummaryItem}
                aria-label={`Delivery fee: $${order.charges.fees.delivery}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Delivery fee
                </span>
                <span className={style.SummaryValue} aria-hidden='true'>
                  ${order.charges.fees.delivery}
                </span>
              </div>
              <div
                className={style.SummaryItem}
                aria-label={`Service fee: $${order.charges.fees.service}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Service fee
                </span>
                <span className={style.SummaryValue} aria-hidden='true'>
                  ${order.charges.fees.service}
                </span>
              </div>
              <div
                className={style.SummaryItem}
                aria-label={`Sales tax: $${order.charges.taxes.total}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Sales tax
                </span>
                <span className={style.SummaryValue} aria-hidden='true'>
                  ${order.charges.taxes.total}
                </span>
              </div>
              <div
                className={style.SummaryItem}
                aria-label={`Driver tips: $${order.charges.tip.amount}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Driver tips
                </span>
                <span className={style.SummaryValue} aria-hidden='true'>
                  ${order.charges.tip.amount}
                </span>
              </div>
              <div
                className={style.SummaryItem}
                aria-label={`Donate change to NFB: $${order.moreInfo.amountRoundUp}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Donate change to NFB
                </span>
                <span className={style.SummaryValue} aria-hidden='true'>
                  ${order.moreInfo.amountRoundUp}
                </span>
              </div>
              <div
                className={style.SummaryItem}
                aria-label={`Total discount: minus $${order.moreInfo.amountDiscount}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Discount (Total)
                </span>
                <span className={`${style.SummaryValue} ${style.Discount}`} aria-hidden='true'>
                  -${order.moreInfo.amountDiscount}
                </span>
              </div>
              <div
                className={`${style.SummaryItem} ${style.TotalRow}`}
                aria-label={`Order total: $${calculateCorrectTotal(order)}`}
              >
                <span className={style.SummaryLabel} aria-hidden='true'>
                  Total
                </span>
                <span className={`${style.SummaryValue} ${style.Total}`} aria-hidden='true'>
                  ${calculateCorrectTotal(order)}
                </span>
              </div>
            </div>
          </div>
        )}
        {order.moreInfo.note && showDetails && (
          <div className={style.NotesSection}>
            <VisuallyHidden>
              <h3>Order Notes</h3>
            </VisuallyHidden>
            <div className={style.NotesLabel}>Order Notes</div>
            <div className={style.NotesText}>"{order.moreInfo.note}"</div>
          </div>
        )}
      </div>
      {!showDetails && (
        <div className={style.DeliveryInfo}>
          <VisuallyHidden>
            <h3>Order Status</h3>
          </VisuallyHidden>
          <div className={style.DeliveryStatusContainer}>
            <div className={style.DeliveryStatusLeft}>{getStatusContent()}</div>
            <div className={style.DeliveryStatusRight}>
              <PrimaryButton
                size='lg'
                className={isFinalized ? style.TrackOrderButton : style.ViewDetailButton}
                onPress={() => {
                  void navigate({
                    to: '/food-orders/$foodOrderId',
                    params: {foodOrderId: order.id},
                  })
                }}
                aria-label={
                  isFinalized
                    ? `Track delivery for order ${order.id}`
                    : `View details for order ${order.id}`
                }
              >
                {isFinalized ? 'Track your delivery order' : 'View details'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
